import React, { useState } from 'react';

import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import Sort from '@mui/icons-material/Sort';

import { IDataGridColumn } from '../interfaces/IDataGridColumn';

enum DIR {
  UP = 'up',
  DOWN = 'down',
  DEFAULT = 'default',
}

interface SortState {
  [key: string | number]: {
    direction: DIR;
    key: string | number;
  };
}

// TODO: this hook is not working properly as this gridView component is being rendered multiple times
// As a result, the state from the hooks, is getting reset to default values on every render
export const useGridSorting = <T extends Record<string | number, any>>(
  rows: T[],
  columns: IDataGridColumn[],
) => {
  const [currentSort, setCurrentSort] = useState<SortState>({});
  const [sortedRows, setSortedRows] = useState<T[]>(rows);

  const columnRefs = [columns.map(() => React.createRef<HTMLDivElement>())];

  const mapRowsRefs = (rowsCopy: T[]) => {
    return rowsCopy.map(() =>
      columns.map(() => React.createRef<HTMLDivElement>()),
    );
  };

  const initialRowRefs = mapRowsRefs(rows);
  const [rowsRefs, setRowsRefs] = useState<React.RefObject<HTMLDivElement>[][]>(
    initialRowRefs || [],
  );

  const updateRowsRefs = (sortedRowsCopy: T[]) => {
    setSortedRows(sortedRowsCopy);
    const sorted = mapRowsRefs(sortedRowsCopy);
    setRowsRefs(sorted);
  };

  const SortIcon: React.FC<{ direction: DIR }> = ({ direction }) => {
    switch (direction) {
      case DIR.UP:
        return <ArrowDropUp style={{ marginLeft: '8px', opacity: 1 }} />;
      case DIR.DOWN:
        return <ArrowDropDown style={{ marginLeft: '8px', opacity: 1 }} />;
      default:
        return <Sort style={{ marginLeft: '8px', opacity: 0.5 }} />;
    }
  };

  const getSortDirection = (key: string | number): DIR => {
    if (currentSort[key]?.direction === DIR.UP) {
      return DIR.UP;
    } else if (currentSort[key]?.direction === DIR.DOWN) {
      return DIR.DOWN;
    }
    return DIR.DEFAULT;
  };

  const getSortIcon = (key: string | number): JSX.Element => {
    const sortDirection = getSortDirection(key);
    return <SortIcon direction={sortDirection} />;
  };

  const setSortState = (key: string | number) => {
    let nextSort: DIR;

    if (currentSort[key]) {
      nextSort = currentSort[key].direction === DIR.DOWN ? DIR.UP : DIR.DOWN;
      currentSort[key].direction = nextSort;
    } else {
      nextSort = DIR.DOWN;
      currentSort[key] = { direction: nextSort, key };
    }

    // Reset other columns to default
    const newSortState: SortState = { [key]: { ...currentSort[key] } };
    setCurrentSort(newSortState);
  };

  const defaultSortMethod = (a: any, b: any): number => {
    a = a ?? -Infinity;
    b = b ?? -Infinity;

    a = typeof a === 'string' ? a?.toLowerCase()?.replace(/(^\$|,)/g, '') : a;
    b = typeof b === 'string' ? b?.toLowerCase()?.replace(/(^\$|,)/g, '') : b;

    a = !isNaN(Number(a)) ? Number(a) : a;
    b = !isNaN(Number(b)) ? Number(b) : b;

    if (a > b) return 1;
    if (a < b) return -1;
    return 0;
  };

  const compareBy =
    (
      key: string | number,
      sortMethod: (a: any, b: any) => number = defaultSortMethod,
    ) =>
    (a: T, b: T): number => {
      setSortState(key);

      const nextSort = currentSort[key]?.direction;
      switch (nextSort) {
        case DIR.DOWN:
          return sortMethod(a[key], b[key]);
        case DIR.UP:
          return sortMethod(b[key], a[key]);
        default:
          return 0;
      }
    };

  return {
    rowsRefs,
    columnRefs,
    sortedRows,
    compareBy,
    setSortState,
    updateRowsRefs,
    getSortDirection,
    getSortIcon,
  };
};
